import React, { useState, useEffect } from "react";
import "../style/WhiskiesList.css";

import WhiskeyCard from "./WhiskeyCard";
import SelectComponent from "./SelectComponent";
import BackToTopBtn from "./BackToTopBtn";

import { useSelector, useDispatch } from "react-redux";
import {
  setWhiskies,
  setDistilleries,
  setFilteredWhiskies,
} from "../redux/slices/whiskiesSlice";
const WhiskiesList = () => {
  const dispatch = useDispatch();

  // Access whiskies and loading state from the Redux store.
  const whiskies = useSelector((state) => state.whiskies.whiskies);
  const filteredWhiskies = useSelector(
    (state) => state.whiskies.filteredWhiskies,
  );
  const distilleries = useSelector((state) => state.whiskies.distilleries);
  const user = useSelector((state) => state.user?.user);

  const [searchState, setSearchState] = useState("");
  const [filtersOn, setFiltersOn] = useState(false);
  const [showBackToTopBtn, setShowBackToTopBtn] = useState(false);

  // Fetch whiskies and distilleries directly in useEffect.
  useEffect(() => {
    fetchDistilleriesAndWhiskies();
  }, []);

  /**
   * Sets search state that is used filtering whiskies by distilleries.
   */
  const searchDistilleryHandler = (e) => {
    findByDistillery(e);
  };

  /**
   * Fetch whiskies data from Supabase and dispatch them to the store.
   * Extract distilleries from whiskies data client-side.
   */
  const fetchDistilleriesAndWhiskies = async () => {
    try {
      // Fetch whiskies from Supabase.
      const whiskiesResponse = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/rest/v1/whiskies?select=*&order=created_at.desc`,
        {
          headers: {
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_API_KEY,
          },
        },
      );

      if (!whiskiesResponse.ok) {
        throw new Error("Failed to fetch whiskies");
      }

      const whiskiesData = await whiskiesResponse.json();

      // Transform Supabase format back to your Redux format.
      const transformedWhiskies = whiskiesData.map((whiskey) => ({
        id: whiskey.id,
        whiskeyTitle: whiskey.whiskey_title,
        distillery: whiskey.distillery || [""],
        cask: whiskey.cask || [""],
        alc: whiskey.alc,
        region: whiskey.region,
        country: whiskey.country,
        description: whiskey.description,
        price: whiskey.price,
        tags: whiskey.tags || [],
        isInStock: whiskey.is_in_stock,
      }));

      dispatch(setWhiskies(transformedWhiskies));

      // Extract unique distilleries from whiskies data (client-side).
      const distillerySet = new Set();
      const distilleries = [];

      transformedWhiskies.forEach((whiskey) => {
        if (whiskey.distillery && Array.isArray(whiskey.distillery)) {
          whiskey.distillery.forEach((dist) => {
            if (dist && dist.trim() && !distillerySet.has(dist.trim())) {
              distillerySet.add(dist.trim());
              distilleries.push(dist.trim());
            }
          });
        }
      });

      distilleries.sort((a, b) => a.localeCompare(b));
      dispatch(setDistilleries(distilleries));
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  /**
   * Sets search state that is used filtering whiskies by title.
   */
  const searchTitleHandler = (e) => {
    setSearchState(e.target.value);
  };

  /**
   * Filters whiskies by cask name from whiskies array.
   * "Cask" is a key in whiskey object storing an array.
   */
  const findByCask = (e) => {
    const filtered = whiskies.filter((whiskey) => {
      return (
        Array.isArray(whiskey.cask) &&
        whiskey.cask.some((el) => {
          return el.toLowerCase().includes(e.toLowerCase());
        })
      );
    });
    setFiltersOn(true);
    dispatch(setFilteredWhiskies(filtered));
  };

  /**
   * Filters whiskies by distillery name from whiskies array.
   * "Distillery" is a key in whiskey object storing an array.
   */
  const findByDistillery = (e) => {
    const filtered = whiskies.filter((whiskey) => {
      return whiskey.distillery.some((el) => {
        return el.toLowerCase().includes(e.toLowerCase());
      });
    });
    setFiltersOn(true);
    dispatch(setFilteredWhiskies(filtered));
  };

  /**
   * Filters whiskies by tag value from whiskies array.
   * "Tags" is a key in whiskey object storing an array.
   */
  const findByTag = (e) => {
    const filtered = whiskies.filter((whiskey) => {
      return whiskey.tags.some((el) => {
        return el.toLowerCase().includes(e.toLowerCase());
      });
    });
    setFiltersOn(true);
    dispatch(setFilteredWhiskies(filtered));
  };

  /**
   * Filters whiskies by title from whiskies array.
   * "WhiskeyTitle" is a key in whiskey object storing a string.
   */
  const findByTitle = () => {
    const filtered = whiskies.filter((whiskey) =>
      whiskey.whiskeyTitle.toLowerCase().includes(searchState.toLowerCase()),
    );
    setFiltersOn(true);
    dispatch(setFilteredWhiskies(filtered));
  };

  const resetFilters = () => {
    setFiltersOn(false);
    setSearchState("");
  };

  // Back to Top Function
  const handleScroll = () => {
    const scrollTop = Math.max(
      document.body.scrollTop,
      document.documentElement.scrollTop,
    );
    setShowBackToTopBtn(scrollTop > 400 ? "visible" : "hidden");
  };

  window.addEventListener("scroll", handleScroll);

  const goToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Group whiskies by country
  const groupedWhiskiesNotSorted = (
    filtersOn ? filteredWhiskies : whiskies
  ).reduce((groups, whiskey) => {
    const country = whiskey.country || "Other";
    if (!groups[country]) {
      groups[country] = [];
    }
    groups[country].push(whiskey);
    return groups;
  }, {});

  // Sort country keys, with Scotland, Ireland, Finland, Japan, and USA at the top, and rest under "Others"
  const order = {
    Scotland: -5,
    Ireland: -4,
    Finland: -3,
    Japan: -2,
    USA: -1,
  };
  const sortedEntries = Object.entries(groupedWhiskiesNotSorted).sort(
    ([countryA], [countryB]) => {
      if (order[countryA] !== undefined && order[countryB] !== undefined) {
        return (order[countryA] || 0) - (order[countryB] || 0);
      } else if (order[countryA] !== undefined) {
        return -1;
      } else if (order[countryB] !== undefined) {
        return 1;
      } else {
        return countryA.localeCompare(countryB);
      }
    },
  );

  const groupedWhiskies = sortedEntries.reduce((acc, [country, whiskies]) => {
    if (["Scotland", "Ireland", "Finland", "Japan", "USA"].includes(country)) {
      acc[country] = whiskies.sort((a, b) =>
        a.distillery[0].localeCompare(b.distillery[0]),
      );
    } else {
      acc.Others = acc.Others || [];
      acc.Others.push(
        ...whiskies.sort((a, b) =>
          a.distillery[0].localeCompare(b.distillery[0]),
        ),
      );
    }
    return acc;
  }, {});

  return (
    <section className="whiskieListPageContainer">
      <BackToTopBtn visible={showBackToTopBtn} onClick={goToTop} />
      <img
        className="logo"
        alt="Barley & Bait logo"
        src={require("../img/BARLEY-BAIT_logo_round_white-white.png")}
        onClick={() => window.location.reload()}
      />
      <div className="searchBars">
        {user && (
          <p className="whiskeyCount">
            {" "}
            currently <span>{whiskies.length}</span> whiskies in the house
          </p>
        )}

        <div className="searchContainer form-inline">
          <input
            className="form-control mr-sm-2"
            type="search"
            placeholder="Search by title"
            onChange={searchTitleHandler}
            aria-label="Search"
            value={searchState}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                findByTitle();
              }
            }}
          />
          <button className="searchSubmitButton" onClick={findByTitle}>
            <span className="material-symbols-outlined">search</span>
          </button>
        </div>

        <div className="selectContainer input-group">
          <SelectComponent
            options={distilleries}
            onSearch={searchDistilleryHandler}
          />
        </div>

        <div className="countryLinks">
          <div className="firstRow">
            <a href="#Scotland">Scotland</a>
            <a href="#Ireland">Ireland</a>
            <a href="#Finland">Finland</a>
          </div>
          <div className="secondRow">
            <a href="#USA">USA</a>
            <a className="japan" href="#Japan">
              Japan
            </a>
            <a href="#Others">Others</a>
          </div>
        </div>
        {filtersOn ? (
          <button
            alt="empty filters"
            className="emptyFiltersButton"
            onClick={resetFilters}
          >
            Empty Filters x
          </button>
        ) : (
          <div style={{ visibility: "hidden" }} className="fill"></div>
        )}
      </div>

      {/* Display filtered whiskies if available, otherwise display all whiskies */}
      <div className="whiskeyListContainer">
        {Object.keys(groupedWhiskies).map((country) => (
          <React.Fragment key={country}>
            {groupedWhiskies[country]?.length > 0 && (
              <h2 className="countryLabel" id={country}>
                {country.toUpperCase()}
              </h2>
            )}
            <div className="columns-container">
              <p className="column-header">Name</p>
              <p className="column-country">Region</p>
              <p className="column-distillery">Distillery</p>
              {/*TODO Hide for now, cask is not a column in company Excels.*/}
              {/*<p className="column-cask">Cask</p>*/}
              <p className="column-alc">alc</p>
              <p className="column-price">€ / 2cl</p>
            </div>
            {groupedWhiskies[country]?.map((whiskey, i) => (
              <WhiskeyCard
                whiskey={whiskey}
                key={i}
                tagClicked={(e) => {
                  findByTag(e.target.value);
                }}
                caskClicked={(e) => {
                  findByCask(e.target.value);
                }}
                user={user}
              />
            ))}
          </React.Fragment>
        ))}
      </div>
    </section>
  );
};

export default WhiskiesList;
